import { Badge } from "@/components/ui/badge";
import {
  MASTERING_STATUS_LABELS,
  ORDER_STATUS_LABELS,
  STATE_LABELS,
  type MasteringStatus,
  type OrderFtpStatus,
  type OrderState,
} from "@/lib/orders-api";

export type Tone = "success" | "warning" | "critical" | "info" | "neutral";

/** Status-Badge mit weichem Hintergrund und Punkt in Tonfarbe. */
export function StatusBadge({
  tone,
  children,
  className,
  title,
}: {
  tone: Tone;
  children: React.ReactNode;
  className?: string;
  title?: string;
}) {
  return (
    <Badge variant={tone} className={className} title={title}>
      <span
        aria-hidden
        className="size-1.5 rounded-full bg-current opacity-70"
      />
      {children}
    </Badge>
  );
}

// Grün = abgeschlossen, Gelb = wartet auf einen Menschen, Rot = Fehler,
// Blau = Pipeline arbeitet, Grau = noch nicht angefasst / beendet.
const STATE_TONE: Record<OrderState, Tone> = {
  received: "neutral",
  mapped: "neutral",
  generating_assets: "info",
  assets_ready: "info",
  quality_checking: "info",
  awaiting_affinity: "warning",
  print_files_uploaded: "info",
  awaiting_review: "warning",
  ready_to_order: "info",
  mastering_uploaded: "info",
  title_live: "info",
  order_uploaded: "info",
  order_confirmed: "success",
  shipped: "success",
  on_hold: "warning",
  needs_attention: "critical",
  cancelled: "neutral",
  rejected_in_review: "critical",
};

const MASTERING_TONE: Record<MasteringStatus, Tone> = {
  not_uploaded: "neutral",
  uploaded: "info",
  failed: "critical",
  accepted: "success",
};

const ORDER_FTP_TONE: Record<OrderFtpStatus, Tone> = {
  not_uploaded: "neutral",
  uploaded: "info",
  failed: "critical",
  confirmed: "success",
};

export function OrderStateBadge({ state }: { state: OrderState }) {
  return (
    <StatusBadge tone={STATE_TONE[state]}>{STATE_LABELS[state]}</StatusBadge>
  );
}

export function MasteringStatusBadge({
  status,
  title,
}: {
  status: MasteringStatus;
  title?: string;
}) {
  return (
    <StatusBadge tone={MASTERING_TONE[status]} title={title}>
      {MASTERING_STATUS_LABELS[status]}
    </StatusBadge>
  );
}

export function OrderFtpStatusBadge({
  status,
  title,
}: {
  status: OrderFtpStatus;
  title?: string;
}) {
  return (
    <StatusBadge tone={ORDER_FTP_TONE[status]} title={title}>
      {ORDER_STATUS_LABELS[status]}
    </StatusBadge>
  );
}

/**
 * Druckerei-Fortschritt in einer Badge: Die Order-Datei ist der spätere
 * Schritt, deshalb hat ihr Status Vorrang; vorher zählt das Mastering.
 * Den vollständigen Stand beider Uploads zeigt der Tooltip.
 */
export function PrinterStatusBadge({
  mastering,
  order,
}: {
  mastering: MasteringStatus;
  order: OrderFtpStatus;
}) {
  const title = `Mastering: ${MASTERING_STATUS_LABELS[mastering]} · Order: ${ORDER_STATUS_LABELS[order]}`;
  if (order !== "not_uploaded") {
    return (
      <StatusBadge tone={ORDER_FTP_TONE[order]} title={title}>
        {ORDER_STATUS_LABELS[order]}
      </StatusBadge>
    );
  }
  return (
    <StatusBadge tone={MASTERING_TONE[mastering]} title={title}>
      {mastering === "not_uploaded"
        ? "Noch nichts hochgeladen"
        : MASTERING_STATUS_LABELS[mastering]}
    </StatusBadge>
  );
}
